import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listMyQuotations } from '../api/vendorApi';
import {
	VpSpinner,
	VpEmpty,
	VpStatusBadge,
	VpPagination,
	money,
	fmtDate,
} from '../components/VpUI';

const PER_PAGE = 10;

const VendorMyQuotations = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [rows, setRows] = useState<any[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState('');
	const [status, setStatus] = useState('');
	const [error, setError] = useState('');

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const data = await listMyQuotations({
				pageNo: page,
				perPage: PER_PAGE,
				search: search || undefined,
				status: status || undefined,
			});
			setRows(data?.records || []);
			setTotal(data?.totalRecords || 0);
		} catch (err: any) {
			setError(err?.apiMessage || 'Could not load your quotations.');
		} finally {
			setLoading(false);
		}
	}, [page, search, status]);

	useEffect(() => {
		load();
	}, [load]);

	if (error) return <div className='vp-alert vp-alert-error'>{error}</div>;

	return (
		<div className='vp-card'>
			<div className='vp-card-head'>
				<h3>My Quotations</h3>
				<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
					<input
						className='vp-input vp-input-sm'
						style={{ width: 180 }}
						placeholder='Search quotation no'
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						onKeyDown={(e) => e.key === 'Enter' && setPage(1)}
					/>
					<select
						className='vp-input vp-input-sm'
						style={{ width: 150 }}
						value={status}
						onChange={(e) => {
							setStatus(e.target.value);
							setPage(1);
						}}>
						<option value=''>All statuses</option>
						<option value='pending'>Pending</option>
						<option value='confirmed'>Accepted</option>
						<option value='rejected'>Rejected</option>
					</select>
					<button
						type='button'
						className='vp-btn vp-btn-ghost vp-input-sm'
						onClick={() => setPage(1)}>
						Search
					</button>
				</div>
			</div>
			{loading ? (
				<VpSpinner />
			) : rows.length === 0 ? (
				<VpEmpty icon='quotations' text='No quotations found.' />
			) : (
				<>
					<div className='vp-table-wrap'>
						<table className='vp-table'>
							<thead>
								<tr>
									<th>Quotation No</th>
									<th>Requisition</th>
									<th>Date</th>
									<th style={{ textAlign: 'right' }}>Amount</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((q) => (
									<tr
										key={q._id}
										style={{ cursor: 'pointer' }}
										onClick={() =>
											navigate(`/vendor-portal/quotations/${q._id}`)
										}>
										<td style={{ fontWeight: 600 }}>{q.quotation_no}</td>
										<td>{q.requisition?.requisition_no || '—'}</td>
										<td>{fmtDate(q.quotation_date || q.createdAt)}</td>
										<td style={{ textAlign: 'right' }}>
											{money(q.amount_total)}
										</td>
										<td>
											<VpStatusBadge status={q.quotation_status} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<VpPagination page={page} perPage={PER_PAGE} total={total} onPage={setPage} />
				</>
			)}
		</div>
	);
};

export default VendorMyQuotations;
